import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import {
  CommissioningRemoteDeviceAssignVentilationParams,
  CommissioningStore,
  DeviceAssignmentType,
  InstallationFeaturesStore,
} from '@store';
import { catchError, filter, switchMap, take, takeUntil, tap, timeout } from 'rxjs/operators';
import { Feature, FeatureNames } from '../../../../../../projects/client';
import { InstallationService } from '../../../../services/installation.service';
import { ModalAction } from '../commissioning.model';
import { CommissioningService } from '../commissioning.service';
import { EMPTY, Subject } from 'rxjs';
export enum AssignVentilationState {
  Loading,
  AssignVentilation,
  ErrorNoVentilations,
}

@Component({
  selector: 'app-commissioning-add-ventilation-modal',
  templateUrl: './commissioning-add-ventilation-modal.component.html',
  styleUrls: ['./commissioning-add-ventilation-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CommissioningAddVentilationModalComponent implements OnInit, OnDestroy {
  AssignVentilationState = AssignVentilationState;
  assignVentilationStateLoadingState = AssignVentilationState.Loading;

  allVentilations: Feature<never, never>[];
  selectedOption: Feature<never, never> | null = null;
  pairedDeviceId: string;
  private ngUnsubscribe$: Subject<void> = new Subject<void>();
  constructor(
    public installationService: InstallationService,
    public commissioningService: CommissioningService,
    public installationFeaturesStore: InstallationFeaturesStore,
    private commissioningStore: CommissioningStore,
    private dialogRef: MatDialogRef<CommissioningAddVentilationModalComponent>,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.dialogRef.updateSize('768px');
    this.dialogRef.addPanelClass('commissioning-modal');
    this.selectedOption = null;

    this.commissioningStore.pairedDeviceId$
      .pipe(
        take(1),
        takeUntil(this.ngUnsubscribe$),
        tap(() => {
          this.assignVentilationStateLoadingState = AssignVentilationState.Loading;
        }),
        switchMap((pairedDeviceId) => {
          this.pairedDeviceId = pairedDeviceId;

          return this.installationFeaturesStore
            .selectAllInstallationDeviceRemoteFeatures(
              FeatureNames.ventilationRemoteController,
              pairedDeviceId
            )
            .pipe(
              timeout(5000),
              filter((entry) => !!entry && entry.length > 0),
              take(1),
              catchError(() => {
                this.assignVentilationStateLoadingState =
                  AssignVentilationState.ErrorNoVentilations;
                this.cdr.markForCheck();

                return EMPTY;
              })
            );
        })
      )
      .subscribe((ventilations: Feature<never, never>[]) => {
        this.allVentilations = ventilations;
        this.assignVentilationStateLoadingState = AssignVentilationState.AssignVentilation;
        this.cdr.markForCheck();
      });
  }

  onVentilationOptionSelect(ventilationOption: Feature<never, never>): void {
    this.selectedOption = ventilationOption;
  }

  navigateBack(): void {
    this.commissioningService.modalAction$.next(ModalAction.PreviousStep);
  }

  navigateForward(): void {
    if (this.selectedOption !== null) {
      const assignedVentilation: CommissioningRemoteDeviceAssignVentilationParams = {
        type: DeviceAssignmentType.Ventilation,
        deviceId: this.pairedDeviceId,
        ventilationId: `${this.selectedOption.deviceId}`,
      };
      this.commissioningStore.remoteDeviceAssignVentilation(assignedVentilation);
    }
    this.commissioningService.modalAction$.next(ModalAction.NextStep);
  }

  canNavigateForward(): boolean {
    return (
      this.selectedOption !== null ||
      this.assignVentilationStateLoadingState === AssignVentilationState.ErrorNoVentilations
    );
  }

  closeDialog(): void {
    this.commissioningService.modalAction$.next(ModalAction.Close);
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }
}
